import {
  Flex,
  Box,
  Text,
  IconButton,
  HStack,
  Tooltip,
  VStack
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useLyricsTheme } from "../context/LyricsThemeContext";
import Icon from './Icon';
import minusIcon from '../assets/icons/minus.svg';
import plusIcon from '../assets/icons/plus.svg';
import resetIcon from '../assets/icons/reset.svg';

interface LyricTimingControlsProps {
  lyricsDelay: number;
  setLyricsDelay: (delay: number) => void;
  compact?: boolean;
}

// Define valid multiplier values - ensure they're all numeric
const MULTIPLIER_VALUES = [0.3, 0.7, 1.1];
const DEFAULT_MULTIPLIER = 0.3;

const LyricTimingControls = ({ lyricsDelay, setLyricsDelay, compact = false }: LyricTimingControlsProps) => {
  const { isLightMode, multiplier, setMultiplier } = useLyricsTheme();
  const [delayChangeAmount, setDelayChangeAmount] = useState(100);
  
  // Ensure multiplier is a valid value on component mount
  useEffect(() => {
    if (!MULTIPLIER_VALUES.includes(multiplier)) {
      setMultiplier(DEFAULT_MULTIPLIER);
    }
  }, []);

  const handleDelayLyrics = () => {
    // Multiply by 1000 to convert seconds to milliseconds
    setLyricsDelay(lyricsDelay - Math.round(multiplier * 1000));
  };

  const handleAdvanceLyrics = () => {
    // Multiply by 1000 to convert seconds to milliseconds
    setLyricsDelay(lyricsDelay + Math.round(multiplier * 1000));
  };

  const handleResetDelay = () => {
    setLyricsDelay(0);
  };
  
  // Format delay for display
  const formatDelay = (delay: number) => {
    const sign = delay > 0 ? '+' : '';
    return `${sign}${(delay / 1000).toFixed(1)}s`;
  };

  // Format multiplier for display
  const formatMultiplier = (mult: number) => {
    return `${mult}s`;
  };

  // Handle delay adjustments
  const decreaseDelay = () => {
    setLyricsDelay(lyricsDelay - delayChangeAmount);
  };

  const increaseDelay = () => {
    setLyricsDelay(lyricsDelay + delayChangeAmount);
  };

  const resetDelay = () => {
    setLyricsDelay(0);
  };

  const togglePrecision = () => {
    // Toggle between fine (100ms) and coarse (500ms) adjustments
    setDelayChangeAmount(delayChangeAmount === 100 ? 500 : 100);
  };

  // Calculate the effective delay (what's shown to the user)
  const effectiveDelay = lyricsDelay;
  const displayDelay = effectiveDelay !== 0 ? 
    `${effectiveDelay > 0 ? '+' : ''}${(effectiveDelay/1000).toFixed(1)}s` : 
    "Sync'd";

  // Calculate button dimensions
  const buttonSize = compact ? "48px" : "56px";
  const multiplierButtonSize = compact ? "52px" : "60px";

  // Cycle through multiplier values on click
  const cycleMultiplier = () => {
    const currentIndex = MULTIPLIER_VALUES.indexOf(multiplier);
    const nextIndex = (currentIndex + 1) % MULTIPLIER_VALUES.length;
    setMultiplier(MULTIPLIER_VALUES[nextIndex]);
  };

  // Handle delay changes based on current multiplier
  const handleDecrease = () => {
    // Multiply by 1000 to convert seconds to milliseconds
    setLyricsDelay(lyricsDelay - Math.round(multiplier * 1000));
  };

  const handleIncrease = () => {
    // Multiply by 1000 to convert seconds to milliseconds
    setLyricsDelay(lyricsDelay + Math.round(multiplier * 1000));
  };

  return (
    <Box 
      className={isLightMode ? "light-mode" : "dark-mode"}
      width={compact ? "auto" : "100%"}
      scrollPaddingX={compact ? 2 : 3}
      borderRadius="md"
      bg="var(--color-surface-variant)"
      boxShadow="sm"
    >
      <Flex 
        direction="column"
        align="center" 
        width={compact ? "auto" : "100%"}
        px={compact ? 2 : 4}
      >
        {/* Delay Display */}
        <Text 
          fontSize={compact ? "sm" : "md"} 
          fontWeight="medium"
          textTransform="uppercase"
          letterSpacing="wider"
          mb={2}
          color="var(--color-text-muted)"
        >
          Lyric Timing
        </Text>
        
        {/* Main Controls */}
        <Flex 
          justify="center" 
          align="center" 
          width="100%" 
          mb={2} 
          gap={compact ? 3 : 5}
        >
          {/* Decrease Button */}
          <Tooltip label={`Decrease delay by ${multiplier.toFixed(1)}s`} placement="top">
            <IconButton
              aria-label="Decrease delay"
              icon={<Icon name="minus" size={24} />}
              colorScheme="whiteAlpha"
              bg="transparent"
              color="var(--color-control-foreground)"
              size="lg"
              width={buttonSize}
              height={buttonSize}
              minWidth={buttonSize}
              minHeight={buttonSize}
              borderRadius="full"
              p={0}
              onClick={handleDecrease}
              _hover={{ bg: 'var(--color-background-muted)' }}
              _active={{ transform: 'scale(0.95)' }}
              transition="all 0.2s"
            />
          </Tooltip>
          
          {/* Multiplier Display - clickable to cycle through values */}
          <Tooltip label="Click to change multiplier" placement="top">
            <Box
              bg="transparent"
              borderRadius="full"
              width={compact ? "40px" : "50px"}
              textAlign="center"
              onClick={cycleMultiplier}
              cursor="pointer"
              p={1}
              _hover={{ bg: "var(--color-background-muted)" }}
              _active={{ transform: 'scale(0.95)' }}
            >
              <Text
                fontSize={compact ? "lg" : "xl"}
                fontWeight="bold"
                lineHeight="1"
                color="var(--color-text-muted)"
              >
                {multiplier.toFixed(1)}x
              </Text>
            </Box>
          </Tooltip>
          
          {/* Delay Display */}
          <Text 
            fontSize={compact ? "xl" : "2xl"} 
            fontWeight="bold" 
            color={effectiveDelay > 0 ? "green.300" : effectiveDelay < 0 ? "red.300" : "var(--color-text-muted)"}
            minWidth={compact ? "80px" : "100px"}
            textAlign="center"
            lineHeight="1"
          >
            {displayDelay}
          </Text>

          
          {/* Reset Button */}
          <Tooltip label="Reset timing" placement="top">
            <IconButton
              color="var(--color-control-foreground)"
              aria-label="Reset timing"
              icon={<Icon name="reset" size={24} />}
              colorScheme="whiteAlpha"
              bg="transparent"
              size="lg"
              width={buttonSize}
              height={buttonSize}
              minWidth={buttonSize}
              minHeight={buttonSize}
              borderRadius="full"
              p={0}
              onClick={handleResetDelay}
              _hover={{ bg: 'var(--color-background-muted)' }}
              _active={{ transform: 'scale(0.95)' }}
              transition="all 0.2s"
            />
          </Tooltip>
          
          {/* Increase Button */}
          <Tooltip label={`Increase delay by ${multiplier.toFixed(1)}s`} placement="top">
            <IconButton
              color="var(--color-control-foreground)"
              aria-label="Increase delay"
              icon={<Icon name="plus" size={24} />}
              colorScheme="whiteAlpha"
              bg="transparent"
              size="lg"
              width={buttonSize}
              height={buttonSize}
              minWidth={buttonSize}
              minHeight={buttonSize}
              borderRadius="full"
              p={0}
              onClick={handleIncrease}
              _hover={{ bg: 'var(--color-background-muted)' }}
              _active={{ transform: 'scale(0.95)' }}
              transition="all 0.2s"
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Box>
  );
};

export default LyricTimingControls; 